import {Box, BoxProps, Typography, useTheme} from '@mui/material';
import NumberLineGraph from './graphTypes/NumberLine/NumberLineGraph';
import ScatterPlotGraph from './graphTypes/Scatter/ScatterPlotGraph';
import {parsePebblePlotJson} from './utils/parsePebblePlotJson';
import {pebbleplotJsonSchema} from './utils/pebbleplotSchema';

export interface PebblePlotRendererProps extends BoxProps {
  jsonString: string;
}

/**
 * Renders a graph using PebblePlot JSON specification.
 *
 * JSON format follows PebblePlot specification:
 * {
 *   "type": "numberline",
 *   "range": [0, 10],
 *   "lines": [
 *     {"value": 3, "open": false, "label": "x = 3"},
 *     {"value": 7, "open": true, "label": "x > 7", "color": "#1976d2"}
 *   ]
 * }
 */
export default function PebblePlotRenderer({jsonString, sx, ...boxProps}: PebblePlotRendererProps) {
  const theme = useTheme();

  // Helper to resolve color (theme color or CSS color)
  const resolveColor = (color: string): string => {
    if (color.startsWith('#') || color.startsWith('rgb')) {
      return color;
    }
    // Try to resolve as theme color
    const colorParts = color.split('.');
    if (colorParts.length === 2) {
      const [paletteKey, colorKey] = colorParts;
      const palette = theme.palette[paletteKey as keyof typeof theme.palette];
      if (palette && typeof palette === 'object' && colorKey in palette) {
        return (palette as any)[colorKey];
      }
    }
    return color;
  };

  try {
    // Parse JSON (handles comments)
    const parsed = parsePebblePlotJson(jsonString);
    if (!parsed || typeof parsed !== 'object') {
      return (
        <Box
          sx={{
            p: 2,
            color: 'error.main',
            border: '1px solid',
            borderColor: 'error.main',
            borderRadius: 1,
            ...sx,
          }}
          {...boxProps}
        >
          Invalid JSON configuration
        </Box>
      );
    }

    // Validate with Zod schema
    const validationResult = pebbleplotJsonSchema.safeParse(parsed);
    if (!validationResult.success) {
      const errorMessages = validationResult.error.issues
        .map((issue) => `${issue.path.join('.')}: ${issue.message}`)
        .join(', ');
      return (
        <Box
          sx={{
            p: 2,
            color: 'error.main',
            border: '1px solid',
            borderColor: 'error.main',
            borderRadius: 1,
            ...sx,
          }}
          {...boxProps}
        >
          Invalid PebblePlot JSON: {errorMessages}
        </Box>
      );
    }

    const pebblePlotJson = validationResult.data;

    // Convert container properties to sx format
    const containerSx = pebblePlotJson.container
      ? {
          ...(pebblePlotJson.container.width !== undefined && {
            width: pebblePlotJson.container.width,
          }),
          ...(pebblePlotJson.container.height !== undefined && {
            height: pebblePlotJson.container.height,
          }),
          ...(pebblePlotJson.container.backgroundColor !== undefined && {
            backgroundColor: pebblePlotJson.container.backgroundColor,
          }),
          ...(pebblePlotJson.container.border !== undefined && {
            border: pebblePlotJson.container.border,
          }),
        }
      : {};

    // Convert title properties to sx format
    const titleSx = pebblePlotJson.title
      ? {
          ...(pebblePlotJson.title.fontSize !== undefined && {
            fontSize: pebblePlotJson.title.fontSize,
          }),
          ...(pebblePlotJson.title.color !== undefined && {
            color: resolveColor(pebblePlotJson.title.color),
          }),
          ...(pebblePlotJson.title.offsetX !== undefined && {
            marginLeft: pebblePlotJson.title.offsetX,
          }),
          ...(pebblePlotJson.title.offsetY !== undefined && {
            marginTop: pebblePlotJson.title.offsetY,
          }),
          ...(pebblePlotJson.title.padding !== undefined && {
            padding: pebblePlotJson.title.padding,
          }),
          ...(pebblePlotJson.title.margin !== undefined && {
            margin: pebblePlotJson.title.margin,
          }),
        }
      : {};

    // Common wrapper Box for all graph types
    const graphContent = (() => {
      switch (pebblePlotJson.type) {
        case 'numberline':
          return <NumberLineGraph range={pebblePlotJson.range} lines={pebblePlotJson.lines || []} />;
        case 'scatter':
          return (
            <ScatterPlotGraph
              data={pebblePlotJson.data}
              xAxis={pebblePlotJson.xAxis}
              yAxis={pebblePlotJson.yAxis}
              y2Axis={pebblePlotJson.y2Axis}
              legend={pebblePlotJson.legend}
              line={pebblePlotJson.line}
              equationLines={pebblePlotJson.equationLines}
              svgPaths={pebblePlotJson.svgPaths}
              width={pebblePlotJson.width}
              height={pebblePlotJson.height}
            />
          );
      }
    })();

    return (
      <Box
        sx={{
          width: '100%',
          mb: 2,
          ...containerSx,
          ...sx,
        }}
        {...boxProps}
      >
        {pebblePlotJson.title && (
          <Typography
            component="div"
            sx={{
              fontWeight: 600,
              mb: 1,
              ...titleSx,
            }}
          >
            {pebblePlotJson.title.text}
          </Typography>
        )}
        {graphContent}
      </Box>
    );
  } catch (error) {
    return (
      <Box
        sx={{
          p: 2,
          color: 'error.main',
          border: '1px solid',
          borderColor: 'error.main',
          borderRadius: 1,
          ...sx,
        }}
        {...boxProps}
      >
        Failed to render PebblePlot: {error instanceof Error ? error.message : String(error)}
      </Box>
    );
  }
}
